import React, { useCallback, useEffect, useState } from "react";
import { Eye, EyeOff, MessageCircle, PartyPopper, Pencil, Trash2, Trophy, X } from "lucide-react";
import { deleteMonthlyDraw, getMonthlyDraw, runMonthlyDraw, saveMonthlyDraw } from "../../services/rewards";
import { subirImagenEn } from "../../config/firebase";
import { buildWhatsAppUrl } from "../../config/store";
import ImageDropzone, { existingImages } from "./ImageDropzone";
import "./MonthlyDrawPanel.css";

const currentPeriod = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const emptyForm = (period) => ({ period, comboNombre: "", comboDescripcion: "", estado: "borrador" });

export default function MonthlyDrawPanel({ flash }) {
  const [form, setForm] = useState(emptyForm(currentPeriod()));
  const [images, setImages] = useState([]);
  const [draw, setDraw] = useState(null);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getMonthlyDraw({ period: form.period });
      const exists = Boolean(data.comboNombre) && data.estado !== "eliminado";
      setDraw(exists ? data : { period: data.period, participantes: data.participantes || 0 });
      setForm((current) => exists ? {
        ...current,
        comboNombre: data.comboNombre || "",
        comboDescripcion: data.comboDescripcion || "",
        estado: data.estado === "publicado" ? "publicado" : data.estado === "realizado" ? "realizado" : "borrador",
      } : emptyForm(current.period));
      setImages(exists ? existingImages(Array.isArray(data.imagenes) ? data.imagenes : []) : []);
      setEditing(!exists);
    } catch (error) {
      flash("No fue posible cargar el sorteo: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  }, [form.period, flash]);

  useEffect(() => { load(); }, [load]);

  const save = async (event) => {
    event.preventDefault();
    if (!images.length) return flash("Agrega al menos una imagen del premio mensual.", "error");
    setLoading(true);
    try {
      const uploaded = [];
      for (const [index, image] of images.entries()) {
        if (image.file) {
          const result = await subirImagenEn(image.file, `sorteos/${form.period}`);
          uploaded.push({ ...result, orden: index, alt: form.comboNombre });
        } else {
          uploaded.push({ url: image.url, path: image.path || "", orden: index, alt: form.comboNombre });
        }
      }
      await saveMonthlyDraw({ ...form, imagenes: uploaded });
      flash(form.estado === "publicado" ? "Premio mensual actualizado y publicado" : "Premio mensual guardado como borrador");
      await load();
    } catch (error) {
      flash(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const cancelEdit = () => {
    if (!draw?.comboNombre) return;
    setForm((current) => ({
      ...current,
      comboNombre: draw.comboNombre || "",
      comboDescripcion: draw.comboDescripcion || "",
      estado: draw.estado === "publicado" ? "publicado" : "borrador",
    }));
    setImages(existingImages(draw.imagenes || []));
    setEditing(false);
  };

  const remove = async () => {
    if (!window.confirm(`¿Eliminar el premio de ${form.period}? Dejará de mostrarse en la tienda y sus imágenes se eliminarán.`)) return;
    setLoading(true);
    try {
      await deleteMonthlyDraw({ period: form.period });
      flash("Premio mensual eliminado correctamente");
      setDraw({ period: form.period, participantes: draw?.participantes || 0 });
      setForm(emptyForm(form.period));
      setImages([]);
      setEditing(true);
    } catch (error) {
      flash(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const run = async () => {
    if (!window.confirm(`¿Realizar ahora el sorteo de ${form.period}? Esta acción no se puede repetir.`)) return;
    setLoading(true);
    try {
      const result = await runMonthlyDraw({ period: form.period });
      flash("Sorteo realizado correctamente");
      setDraw((current) => ({ ...current, ganador: result.ganador, participantes: result.participantes, estado: "realizado" }));
      setForm((current) => ({ ...current, estado: "realizado" }));
    } catch (error) {
      flash(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const hasPrize = Boolean(draw?.comboNombre);
  const winnerMessage = draw?.ganador ? `Hola ${draw.ganador.nombre}. ¡Felicitaciones! Tu guía ${draw.ganador.guia} ganó el sorteo mensual de ROMI STORE: ${draw.comboNombre}. Escríbenos para coordinar la entrega de tu premio.` : "";

  return <section className="admin-panel monthly-draw-panel">
    <div className="admin-panel__head"><div><span>Sorteo de fin de mes</span><h3>Premio o kit mensual</h3></div><PartyPopper /></div>
    <div className="monthly-period-switch">
      <label>Consultar periodo<input type="month" value={form.period} onChange={(event) => setForm(emptyForm(event.target.value))} disabled={loading} /></label>
      <span><b>{draw?.participantes || 0}</b> guías aprobadas participan</span>
    </div>

    {hasPrize && !editing ? <div className="monthly-prize-summary">
      <div className="monthly-prize-summary__top">
        <div><span className={`monthly-status ${draw.estado}`}>{draw.estado === "publicado" ? <><Eye /> Publicado</> : draw.estado === "realizado" ? <><Trophy /> Sorteado</> : <><EyeOff /> Borrador</>}</span><h4>{draw.comboNombre}</h4><p>{draw.comboDescripcion}</p></div>
        {!draw.ganador && <div className="monthly-summary-actions">
          <button type="button" onClick={() => setEditing(true)} disabled={loading}><Pencil />Editar o actualizar</button>
          <button type="button" className="danger" onClick={remove} disabled={loading}><Trash2 />Eliminar</button>
        </div>}
      </div>
      <div className="monthly-prize-summary__gallery">{(draw.imagenes || []).map((image, index) => <img key={image.path || image.url || index} src={image.url || image} alt={image.alt || draw.comboNombre} />)}</div>
      <div className="monthly-summary-note">{draw.estado === "publicado" ? "Este premio está visible como publicidad en la tienda y en la página de premios." : draw.estado === "realizado" ? "El sorteo fue realizado. Sus datos quedan bloqueados como registro." : "Este premio está guardado, pero todavía no es visible para los clientes."}</div>
    </div> : <form onSubmit={save}>
      <div className="monthly-fields"><label>Periodo<input type="month" value={form.period} disabled required /></label><label>Visibilidad<select value={form.estado} onChange={(event) => setForm({ ...form, estado: event.target.value })}><option value="borrador">Borrador · oculto</option><option value="publicado">Publicado en la tienda</option></select></label></div>
      <label>Nombre del premio<input value={form.comboNombre} onChange={(event) => setForm({ ...form, comboNombre: event.target.value })} placeholder="Ej. Kit Hogar ROMI" required /></label>
      <label>Descripción<textarea rows="4" value={form.comboDescripcion} onChange={(event) => setForm({ ...form, comboDescripcion: event.target.value })} placeholder="Indica claramente todo lo que recibirá el ganador." required /></label>
      <ImageDropzone items={images} onChange={setImages} maxFiles={3} label="Imágenes del premio" onError={(message) => flash(message, "error")} />
      <div className="monthly-draw-count"><span><b>{images.length}</b> de 3 imágenes</span><span><Eye /><b>{form.estado === "publicado" ? "Visible" : "Oculto"}</b> para clientes</span></div>
      <div className="monthly-form-actions">{hasPrize && <button type="button" className="admin-secondary" onClick={cancelEdit} disabled={loading}><X />Cancelar</button>}<button className="admin-primary" disabled={loading}>{loading ? "Procesando..." : form.estado === "publicado" ? hasPrize ? "Actualizar y publicar" : "Guardar y publicar premio" : hasPrize ? "Actualizar como borrador" : "Guardar borrador"}</button></div>
    </form>}

    {hasPrize && <div className="monthly-draw-action">
      {draw?.ganador ? <div className="monthly-winner"><Trophy /><div><small>Ganador del periodo</small><strong>{draw.ganador.nombre}</strong><span>Guía {draw.ganador.guia} · {draw.ganador.telefono}</span><a href={buildWhatsAppUrl(winnerMessage)} target="_blank" rel="noreferrer"><MessageCircle />Contactar ganador</a></div></div> : <><p>Cuando cierre el mes, el sistema elegirá al azar una guía aprobada. El resultado quedará bloqueado y registrado.</p><button type="button" onClick={run} disabled={loading || draw?.estado !== "publicado" || !draw?.imagenes?.length}><Trophy />Realizar sorteo</button></>}
    </div>}
  </section>;
}
